<template>
  <ui-component-modal
    :modalTitle="
      $t(`Components.Services.WebHook.ModalSave.${action}_Main_Title`)
    "
    confirmButtonCssClass="is-success"
    :onClickCancel="onClickCancel"
    :disableSaveButton="isSaving"
    :showModal="showModal"
    :hideFooterButtons="isSavingError || isSavingSuccess"
    :buttonLabelConfirm="
      action === 'Create' ? $t('Form.Control.Create') : $t('Form.Control.Save')
    "
    :isSaving="isSaving"
    :isSavingError="isSavingError"
    :isSavingSuccess="isSavingSuccess"
    :onClickSave="saveWebHook"
    @closeModal="onClickCancel"
    modalSize="large"
  >
    <template v-slot:content>
      <div class="columns">
        <div class="column is-half">
          <h1 class="title">
            {{
              $t(
                'Components.Services.WebHook.ModalSave.Configuration_Sub_Title'
              )
            }}
          </h1>
          <BaseSelectField
            :label="
              $t('Components.Services.WebHook.TableListLabels.Type_Label')
            "
            :error="typeValidationErrors.toString()"
          >
            <select v-model="mWebHook.Type">
              <option
                selected
                value=""
                disabled
                hidden
              >{{$t('Components.Services.WebHook.SaveWebHook_PlaceHolder.SelectType')}}</option>
              <option
                v-for="(type, index) in whitelistedSaveEntityTypes"
                :key="index"
                :value="type"
              >
                {{ $t(`General.Entities.${type}`) }}
              </option>
            </select>
          </BaseSelectField>
          <BaseSelectField
            v-if="filteredActionTypes.length > 0"
            :label="
              $t('Components.Services.WebHook.TableListLabels.Action_Label')
            "
            :error="actionValidationErrors"
          >
            <select v-model="mWebHook.Action">
              <option
                v-if="filteredActionTypes.length && !filteredActionTypes.includes(mWebHook.Action)"
                selected
                :value="mWebHook.Action"
                disabled
                hidden
              >
                {{  $t('Components.Services.WebHook.SaveWebHook_PlaceHolder.SelectAction') }}</option>
              <option
                v-if="!filteredActionTypes.length"
                selected
                value=''
                disabled
                hidden
              >
                {{  $t('Components.Services.WebHook.SaveWebHook_PlaceHolder.SelectTypeFirst') }}</option>
              <option
                v-for="(action, index) in filteredActionTypes"
                :key="index"
                :value="action"
                @input="$v.mWebHook.Action.$touch()"
                @blur="$v.mWebHook.Action.$touch()"
              >
                {{ $t(`General.WebHookEventTypes.${action}_Action`) }}
              </option>
            </select>
          </BaseSelectField>
          <BaseInputField
            v-model="mWebHook.Url"
            :label="$t('Components.Services.WebHook.TableListLabels.Url_Label')"
            :placeholder="'http://www.example.com'"
            :error="urlValidationErrors.toString()"
            @input="$v.mWebHook.Url.$touch()"
            @blur="$v.mWebHook.Url.$touch()"
          />
          <BaseSelectField
            :label="
              $t('Components.Services.WebHook.TableListLabels.Verb_Label')
            "
            :error="methodValidationErrors.toString()"
          >
            <select
              v-model="mWebHook.Verb"
              required
            >
              <option
                selected
                value=""
                disabled
                hidden
              >{{ $t('Components.Services.WebHook.SaveWebHook_PlaceHolder.SelectVerb') }}</option>
              <option>POST</option>
              <option>PUT</option>
              <option>DELETE</option>
            </select>
          </BaseSelectField>
        </div>
        <div class="column is-half">
          <h1 class="title">
            {{ $t('Components.Services.WebHook.ModalSave.Headers_Sub_Title') }}
          </h1>
          <table class="table">
            <thead>
              <tr>
                <th>
                  {{
                    $t('Components.Services.WebHook.Headers.HeaderName_Label')
                  }}
                </th>
                <th>
                  {{
                    $t('Components.Services.WebHook.Headers.HeaderValue_Label')
                  }}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(v, index) in $v.mWebHook.Headers.$each.$iter"
                :key="index"
              >
                <td>
                  <BaseInputField
                    v-model="v.HeaderName.$model"
                    :error="headerValidationErrors(v.HeaderName)"
                  />
                </td>

                <td>
                  <BaseInputField
                    v-model="v.HeaderValue.$model"
                    :error="headerValidationErrors(v.HeaderValue)"
                  />
                </td>
                <td>
                  <a @click="onRemoveHeaderClick(index)">
                    <span class="icon has-text-danger">
                      <font-awesome-icon :icon="['fas', 'trash-alt']" />
                    </span>
                  </a>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td>
                  <BaseInputField
                    v-model="addHeader.HeaderName"
                    placeholder="Content-Type"
                  />
                </td>
                <td>
                  <BaseInputField
                    v-model="addHeader.HeaderValue"
                    placeholder="application/json"
                  />
                </td>
                <td>
                  <button
                    class="button is-ghost is-primary is-small"
                    @click="onAddHeaderClick"
                    :disabled="
                      addHeader.HeaderName.length === 0 ||
                      addHeader.HeaderValue.length === 0
                    "
                  >{{ $t('Form.Control.Add') }}</button>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </template>
  </ui-component-modal>
</template>

<script>
import { mapState } from 'vuex'
import { required, url } from 'vuelidate/lib/validators';
import { cleanSource } from '@/utils/objectHelper'
import webHookProvider from '@/providers/webHook'
import { EventBus } from '@/eventbus/event-bus'

export default {
  name: 'ModalSaveWebHook',

  components: {
    BaseInputField: () => import('@/components/UI/Form/BaseInputField'),
    BaseSelectField: () => import('@/components/UI/Form/BaseSelectField'),
  },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },

    onClickCancel: {
      type: Function,
      required: true,
    },

    onClickAddFinal: {
      type: Function,
      required: true,
    },

    webHook: {
      type: Object,
      default: () => {
        return {
          transformationTemplate: '',
          Meetingtype: 0,
          Headers: [],
          Type: '',
          Action: '',
          Verb: '',
        }
      },
    },

    action: {
      type: String,
      required: true,
    },

    actionKeys: {
      type: Array,
      required: true,
    },
    whitelistedSaveEntityTypes: {
      type: Array,
      required: true,
    }
  },

  data() {
    return {
      isTesting: false,
      loadingDone: false,

      isSavingError: false,
      isSavingSuccess: false,
      isSaving: false,

      addHeader: {
        HeaderName: '',
        HeaderValue: '',
      },

      mWebHook: null,
    }
  },

  /**
   * Form validation rules
   */
  validations() {
    let output = {
      Type: {
        required,
      },
      Action: {
        required,
        reselect: () => this.filteredActionTypes.includes(this.mWebHook.Action),
      },
      Url: {
        required,
        url,
      },
      Verb: {
        required,
      },
      Headers: {
        $each: {
          HeaderName: {
            required,
          },
          HeaderValue: {
            required,
          },
        },
      },
    }

    return {
      mWebHook: output,
    }
  },

  computed: {
    ...mapState('locationStore', ['locationState']),
    filteredActionTypes() {
      if (!this.mWebHook.Type) return []

      return this.actionKeys.filter((actionType) =>
        actionType.includes(this.mWebHook.Type)
      )
    },

    typeValidationErrors() {
      const errors = []
      if (!this.$v.mWebHook.Type.$dirty) return errors

      if (!this.$v.mWebHook.Type.required)
        errors.push(this.$t('Form.InputErrors.Required'))

      return errors
    },
    actionValidationErrors() {
      const errors = []
      if (!this.$v.mWebHook.Url.$dirty) return errors

      if (!this.$v.mWebHook.Action.required)
        errors.push(this.$t('Form.InputErrors.Required'))

      if (this.$v.mWebHook.Action.required && !this.$v.mWebHook.Action.reselect)
        errors.push(this.$t('Form.InputErrors.ReselectOption'))

      return errors
    },
    urlValidationErrors() {
      const errors = []
      if (!this.$v.mWebHook.Url.$dirty) return errors

      if (!this.$v.mWebHook.Url.required)
        errors.push(this.$t('Form.InputErrors.Required'))
      if (!this.$v.mWebHook.Url.url)
        errors.push(this.$t('Form.InputErrors.InvalidUrl'))
      return errors
    },
    methodValidationErrors() {
      const errors = []
      if (!this.$v.mWebHook.Verb.$dirty) return errors

      if (!this.$v.mWebHook.Verb.required)
        errors.push(this.$t('Form.InputErrors.Required'))

      return errors
    },
  },

  created() {
    this.mWebHook = cleanSource(this.webHook)
  },

  methods: {
    saveWebHook() {
      if (this.isSaving) return;

      this.isSaving = true
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.isSaving = false
      } else {
        if (!this.mWebHook.LocationId)
          this.mWebHook.LocationId = this.locationState.Id

        webHookProvider.methods
          .saveWebHook(this.mWebHook)
          .then(() => {
            this.onClickAddFinal()

            this.isSavingSuccess = true
            EventBus.$emit('showToast', {
              type: 'info',
              message: this.$t(`Components.Services.WebHook.ModalSave.${this.action}_SavingSuccess`),
            })

            this.onClickCancel()
          })
          .catch(() => {
            this.isSavingError = true
            this.isSaving = false
          })
      }
    },

    onAddHeaderClick() {
      this.mWebHook.Headers.push(this.addHeader)
      this.addHeader = {
        HeaderName: '',
        HeaderValue: '',
      }
    },
    onRemoveHeaderClick(index) {
      this.mWebHook.Headers.splice(index, 1)
    },

    headerValidationErrors(header) {
      const errors = []

      if (!header.required) errors.push(this.$t('Form.InputErrors.Required'))
      return errors
    },
  },
}
</script>

<style scoped>
/* select:invalid,
select option[value=""] {
  color: #999999;
} */
</style>
